import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/context/LanguageContext";
import { adminLogin, adminVerify } from "@/lib/apiClient";

export default function AdminLogin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If a valid httpOnly session cookie already exists, skip the login form.
  useEffect(() => {
    let cancelled = false;
    adminVerify()
      .then(() => { if (!cancelled) navigate("/admin"); })
      .catch(() => { /* not signed in: stay on the login page */ });
    return () => { cancelled = true; };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(password); // backend sets the httpOnly session cookie
      navigate("/admin");
    } catch {
      setError(t.admin.wrongPassword);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--brand-offwhite)] px-4">
      <div className="absolute inset-0 -z-0 facet-soft" />
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative z-10 w-full max-w-md rounded-3xl border border-[color:var(--border-subtle)] bg-white p-8 shadow-[var(--shadow-float)]"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--surface-2)]">
            <img src="/assets/nina-mark.png" alt="Nina" className="h-9 w-9 object-contain" />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-[var(--brand-navy)]">{t.admin.loginTitle}</h1>
            <p className="text-xs text-[var(--muted-ink)]">Nina Latinos Multi Services</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-[var(--muted-ink)]">{t.admin.loginSubtitle}</p>

        <form onSubmit={submit} className="mt-6" data-testid="admin-login-form">
          <Label htmlFor="password" className="text-sm font-medium text-[var(--brand-charcoal)]">{t.admin.password}</Label>
          <div className="relative mt-1.5">
            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-ink)]" />
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 rounded-xl pl-9"
              placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
              data-testid="admin-password-input"
              autoFocus
            />
          </div>
          {error && <p className="mt-2 text-sm text-[var(--danger)]" data-testid="admin-login-error">{error}</p>}

          <Button type="submit" disabled={loading || !password} className="mt-5 h-11 w-full rounded-xl bg-[var(--brand-burgundy)] font-semibold text-white hover:bg-[var(--brand-burgundy-dark)] disabled:opacity-60" data-testid="admin-login-submit-button">
            {loading ? t.admin.signingIn : (<><LogIn className="mr-2 h-4 w-4" />{t.admin.signIn}</>)}
          </Button>
        </form>

        <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--muted-ink)] transition-colors hover:text-[var(--brand-burgundy)]" data-testid="admin-back-link">
          <ArrowLeft className="h-4 w-4" /> {t.admin.backToSite}
        </Link>
      </motion.div>
    </div>
  );
}
